import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';
export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  semanticId?: string;
  error?: boolean;
  valid?: boolean;
}

const transientProps = ['$error', '$valid'] as const;

const shouldForwardTransient = (prop: string | number): boolean =>
  !transientProps.includes(prop as (typeof transientProps)[number]);

const StyledTextArea = styled.textarea.withConfig({ shouldForwardProp: shouldForwardTransient })<{
  $error: boolean;
  $valid: boolean;
}>`
  background-color: var(--ui-surface);
  border: ${rem(1)} solid var(--ui-input-border, var(--ui-border));
  border-radius: var(--ui-radius-md, ${rem(6)});
  padding: ${rem(11)} ${rem(12)};
  -webkit-appearance: none;

  font-weight: 400;
  font-size: ${rem(15)};
  line-height: ${rem(16)};
  font-family: var(--ui-font-sans);

  filter: drop-shadow(${rem(0)} ${rem(2)} ${rem(4)} var(--ui-input-shadow, var(--ui-shadow-color)));
  transition: all 0.2s 0s ease;
  min-width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
  resize: vertical;

  &:hover,
  &:focus {
    border: ${rem(1)} solid var(--ui-input-border-focus, var(--ui-interactive));
    outline: none;
  }

  ${({ $error }) =>
    $error &&
    css`
      &:not(:disabled) {
        border: ${rem(1)} solid var(--ui-status-danger);
      }
    `}

  ${({ $valid }) =>
    $valid &&
    css`
      &:not(:disabled) {
        border: ${rem(1)} solid var(--ui-status-success);
      }
    `}

  &:disabled {
    background-color: var(--ui-background-muted);
    border: ${rem(1)} solid var(--ui-border);
    color: var(--ui-text);
    -webkit-text-fill-color: var(--ui-text);
    cursor: not-allowed;
    opacity: 1;
    filter: none;
  }

  &:disabled::placeholder {
    color: var(--ui-text-subtle);
    -webkit-text-fill-color: var(--ui-text-subtle);
  }

  &::placeholder {
    font-weight: 400;
    color: var(--ui-text-subtle);
  }
`;

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { semanticId, error = false, valid = false, disabled, ...rest },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <StyledTextArea
      ref={ref}
      $error={error}
      $valid={valid}
      disabled={disabled}
      aria-invalid={error || undefined}
      data-error={error || undefined}
      data-valid={valid || undefined}
      data-disabled={disabled || undefined}
      {...semanticProps}
      {...rest}
    />
  );
});

TextArea.displayName = 'TextArea';
